import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { ConfigEntryType } from 'config/config';
import { useConfigEntryOptionsQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import { useEffect, useState } from 'react';
import CheckboxLineIcon from 'remixicon-react/CheckboxLineIcon';
import { theme } from 'config/theme';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';

type Props = { field: ConfigEntryType };

export default function TaskTypeMultiSelectField({ field }: Props) {
  const { companyId } = useUserData();
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();
  const selectedValues: string[] = useWatch({ control, name: field.entryCode, defaultValue: [] });
  const [fieldValue, setFieldValue] = useState<any[]>([]);
  const { activeUniqueOrderedOptions: options, isLoading } = useConfigEntryOptionsQuery({
    companyId,
    configEntry: field,
    defaultOptions: [],
  });

  useEffect(() => {
    if (options?.length && selectedValues?.length) {
      const optionsMap = new Map(options.map((item) => [item.value, item]));
      setFieldValue(
        selectedValues.map((value) => optionsMap.get(value) || { value, label: value }),
      );
    } else {
      setFieldValue([]);
    }
  }, [options, selectedValues]);

  const handleChange = (_: any, values: any[]) => {
    setFieldValue(values);
    setValue(
      field.entryCode,
      values.map((item) => item.value),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <Controller
      name={field.entryCode}
      control={control}
      render={({ fieldState }) => (
        <Autocomplete
          id={field.entryCode}
          autoHighlight
          multiple
          limitTags={3}
          disableCloseOnSelect
          options={options}
          getOptionLabel={(option) => option.label || option.value}
          value={fieldValue}
          onChange={handleChange}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          filterOptions={(options, state) => {
            const inputValue = state.inputValue.toLowerCase();
            return options.filter(
              (option) =>
                option.label.toLowerCase().includes(inputValue) ||
                option.value.toLowerCase().includes(inputValue),
            );
          }}
          loading={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.value}>
              <CheckboxLineIcon size={18} color={theme.palette.custom.link} />
              <Typography component="span" ml={1}>
                {option.label}
              </Typography>
            </Box>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, idx) => (
              <Chip
                label={option.label}
                icon={<CheckboxLineIcon size={16} color={theme.palette.custom.link} />}
                deleteIcon={<CloseLineIcon size={20} />}
                sx={{ px: 1 }}
                {...getTagProps({ index: idx })}
              />
            ))
          }
          disabled={isSubmitting}
        />
      )}
    />
  );
}
